import { Observable, of, OperatorFunction } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';

/**
 * Search behaviour shared by every HESLP equipment search: the settlement Confirm System Details modal and the
 * pre-sale eligibility check on the referral page (ESC-120). Both surfaces must stay identical, so neither may
 * hard-code these values or re-build the stream.
 */
export const EQUIPMENT_SEARCH_MIN_QUERY_LENGTH = 2;
export const EQUIPMENT_SEARCH_DEBOUNCE_MS = 300;
export const EQUIPMENT_SEARCH_LIMIT = 20;

export interface EquipmentSearchResult<TIn, TResult> {
    input: TIn;
    results: TResult[];
    error: boolean;
}

/** Whether a typed query is long enough to be sent to an equipment search endpoint. */
export function isEquipmentSearchQuery(query: string | null | undefined): boolean {
    return !!query && query.length >= EQUIPMENT_SEARCH_MIN_QUERY_LENGTH;
}

/**
 * Debounces keystrokes and cancels any in-flight request when a newer input arrives, so results can never
 * resolve out of order. Errors are folded into the stream (`error: true`, no results) so it survives a failure
 * and a service error is never reported as "no matches".
 */
export function equipmentSearch<TIn, TResult>(
    search: (input: TIn) => Observable<TResult[]>
): OperatorFunction<TIn, EquipmentSearchResult<TIn, TResult>> {
    return source =>
        source.pipe(
            debounceTime(EQUIPMENT_SEARCH_DEBOUNCE_MS),
            switchMap(input =>
                search(input).pipe(
                    map(results => ({ input, results, error: false })),
                    catchError(() => of({ input, results: <TResult[]>[], error: true }))
                )
            )
        );
}
